import React from "react";
import { Settings, X } from "lucide-react";

interface TableSettingsModalProps {
  isOpen: boolean;
  borderColor: string;
  borderWidth: number;
  borderRadius: number;
  onBorderColorChange: (value: string) => void;
  onBorderWidthChange: (value: number) => void;
  onBorderRadiusChange: (value: number) => void;
  onApply: () => void;
  onClose: () => void;
}

export const TableSettingsModal: React.FC<TableSettingsModalProps> = ({
  isOpen,
  borderColor,
  borderWidth,
  borderRadius,
  onBorderColorChange,
  onBorderWidthChange,
  onBorderRadiusChange,
  onApply,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Table Settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Border Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Table Border Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={borderColor}
                onChange={(e) => onBorderColorChange(e.target.value)}
                className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={borderColor}
                onChange={(e) => onBorderColorChange(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Border Width */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Border Width (px)</label>
            <input
              type="number"
              min="1"
              max="10"
              value={borderWidth}
              onChange={(e) => onBorderWidthChange(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
            />
          </div>

          {/* Border Radius */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Border Radius (px)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="20"
                value={borderRadius}
                onChange={(e) => onBorderRadiusChange(parseInt(e.target.value))}
                className="flex-1"
              />
              <input
                type="number"
                min="0"
                max="50"
                value={borderRadius}
                onChange={(e) => onBorderRadiusChange(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
                className="w-20 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Rounds the corners of the table</p>
          </div>

          {/* Preview */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview</h4>
            <div
              style={{
                border: `${borderWidth}px solid ${borderColor}`,
                borderRadius: `${borderRadius}px`,
                padding: "8px",
                overflow: "hidden",
              }}
              className="text-center text-sm text-gray-600"
            >
              Table border preview
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
          >
            Apply Settings
          </button>
        </div>
      </div>
    </div>
  );
};


